/**
 * Grommet Theme
 *
 * https://v2.grommet.io/globaltheme
 */

import { DropType } from 'grommet';
import { grommet, ThemeType } from 'grommet/themes';
import { Colors } from 'grommet/themes/base';
import { ColorType, deepMerge } from 'grommet/utils';

const talonColors: Colors & { 'brand-gradient': ColorType; 'accent-5': ColorType; 'accent-6': ColorType } = {
  brand: {
    light: '#0d48a1',
    dark: '#0d48a1',
  },
  'brand-gradient': {
    light: 'var(--brand-gradient)',
    dark: 'var(--brand-gradient)',
  },
  'accent-1': '#fff',
  'accent-2': {
    light: '#0d92a1',
    dark: '#0d92a1',
  },
  'accent-3': {
    light: '#a10d48',
    dark: '#a10d48',
  },
  'accent-4': {
    light: '#660da1',
    dark: '#660da1',
  },
  'accent-5': {
    light: '#1c0da1',
    dark: '#1c0da1',
  },
  'accent-6': {
    light: '#a1660d',
    dark: '#a1660d',
  },
};

export const borderRadiusSize = '15px';

const grommetTheme = deepMerge(grommet, {
  global: {
    // Laptop widths (Grommet's medium runs to 1536px): the header folds its global links into "More"
    breakpoints: {
      laptop: { value: 1279 },
    },
    colors: {
      ...talonColors,
      'hint-background': '#DAB8F7FF',
      // Agency UX pass tokens (docs/design_handoff_agency_ux_pass)
      'brand-tint': '#e8eef8',
      'text-heading': '#222',
      'text-label': '#666',
      'text-meta': '#777',
      'text-secondary': '#555',
      'border-layout': '#DADADA',
      'border-card': '#E3E3E3',
      'border-input': '#CFCFCF',
      'border-danger': '#e6b3b3',
      'text-danger': '#c62828',
      'surface-subtle': '#F7F7F7',
      'chart-wages': '#7aa0d8',
      'chart-fringe': '#b9cbe8',
      'bar-ok': '#00a36c',
      'text-empty': '#999',
      'pill-ok-bg': '#dcf5ea',
      'pill-ok-text': '#0b5e3e',
      'pill-warning-bg': '#fdf1dc',
      'pill-warning-text': '#7a4a00',
      'pill-danger-bg': '#fbe3e3',
      // A card about something wrong (e.g. a suspended account)
      'surface-danger': '#fdecec',
      'text-danger-strong': '#8a1c1c',
      'pill-danger-text': '#c62828',
      'pill-neutral-bg': '#EDEDED',
      'pill-neutral-text': '#444',
      'border-divider': '#EDEDED',
      'surface-header': '#F2F2F2',
      focus: talonColors.brand,
      alert: 'red',
    },
    // A focused field: a brand border with a soft tint ring around it
    focus: {
      outline: {
        size: '0px',
      },
      border: { color: 'brand' },
      shadow: { color: 'brand-tint', size: '3px' },
    },
    // Inputs, selects and date pickers: a light grey box with soft corners
    control: {
      border: {
        color: 'border-input',
        radius: '6px',
      },
    },
    input: {
      font: { size: '15px', weight: 400 },
      padding: { horizontal: '10px', vertical: '7px' },
    },
  },
  accordion: {
    border: undefined,
  },
  tip: {
    content: {
      background: {
        color: 'light-1',
        opacity: 1,
      },
      round: borderRadiusSize,
      border: { size: '1px', color: 'light-4' },
      pad: 'small',
      margin: 'small',
    },
  },
  // Tabs: quiet grey labels on the page; the open one is brand and bold, underlined 3px in brand
  tab: {
    color: 'text-secondary',
    active: {
      background: 'transparent',
      color: 'brand',
      weight: 600,
      size: '15px',
    },
    background: 'transparent',
    border: {
      side: 'bottom',
      size: '3px',
      color: 'transparent',
      active: {
        color: 'brand',
      },
      disabled: {
        color: 'transparent',
      },
      hover: {
        color: 'transparent',
      },
    },
    disabled: {
      color: 'text-weak',
    },
    hover: {
      background: 'surface-header',
      color: 'brand',
    },
    pad: { vertical: '12px', horizontal: '14px' },
    margin: 'none',
    // A plain title is Grommet's own Text, which doesn't inherit a size
    extend: '> span { font-size: 15px; line-height: 20px; }',
  },
  tabs: {
    gap: 'none',
    header: {
      border: { side: 'bottom', color: 'border-card' },
    },
  },
  // Font, padding, border and focus ring come from global input/control, so text areas match inputs
  textArea: {
    extend: () => `
    resize: none;
    background: #ffffff;
    color: #000;
    `,
  },
  text: {
    xsmall: {
      size: '12px',
      height: '18px',
    },
    small: {
      size: '14px',
      height: '20px',
    },
    medium: {
      size: '18px',
      height: '24px',
    },
    large: {
      size: '22px',
      height: '28px',
    },
    xlarge: {
      size: '26px',
      height: '32px',
    },
    xxlarge: {
      size: '34px',
      height: '40px',
    },
  },
  dataTable: {
    // Like every table header: a light grey row of small grey labels (see TableHeaderItem)
    header: {
      background: 'surface-header',
      border: { side: 'bottom', color: 'border-card' },
      color: 'text-secondary',
      font: {
        size: 'xsmall',
        weight: '600',
      },
      gap: 'none',
      extend: 'letter-spacing: 0.04em;',
    },

    body: {
      row: {
        extend: 'height: 49px',
      },
    },
  },
  // Every Card looks like TalonCard: a light border instead of a shadow
  card: {
    container: {
      round: '10px',
      elevation: 'none',
      extend: 'border: 1px solid #E3E3E3;',
    },
  },
  // Grommet Table headers, styled like every other table header (see dataTable above)
  table: {
    header: {
      background: 'surface-header',
      border: 'bottom',
    },
  },
  // Form fields are boxed, with a small grey label above (like LabeledField)
  formField: {
    border: { side: 'all', color: 'border-card' },
    round: '6px',
    label: {
      requiredIndicator: true,
      size: 'xsmall',
      color: 'text-label',
      margin: { horizontal: 'none', bottom: '4px', top: 'none' },
    },
    content: { pad: 'none' },
  },
} as ThemeType);

export default grommetTheme;

export const tooltipProps: DropType = {
  background: 'light-1',
  round: borderRadiusSize,
  pad: 'small',
  margin: 'small',
  border: { size: '1px', color: 'light-4' },
  width: { max: grommetTheme.global?.size?.medium },
};
